import React, { useState, createContext, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';

const appConfigContext = createContext(null);
const { Provider } = appConfigContext;

export const useAppConfig = () => useContext(appConfigContext);

export const enrichAppConfigWithBranding = (config) => {
  try {
    const stored = localStorage.getItem('ohif_custom_branding');
    const enriched = { ...config };
    if (stored) {
      const custom = JSON.parse(stored);
      if (custom.websiteTitle !== undefined) {
        enriched.websiteTitle = custom.websiteTitle;
      }
      if (custom.systemName !== undefined) {
        enriched.systemName = custom.systemName;
      }
      if (custom.logoUrl !== undefined) {
        enriched.logoUrl = custom.logoUrl;
      }
      if (custom.faviconUrl !== undefined) {
        enriched.faviconUrl = custom.faviconUrl;
      }
      if (custom.enableAiPanel !== undefined) {
        enriched.enableAiPanel = custom.enableAiPanel;
      }
      if (custom.enableRightPanel !== undefined) {
        enriched.enableRightPanel = custom.enableRightPanel;
      }

      if (custom.systemName || custom.logoUrl) {
        enriched.whiteLabeling = {
          ...enriched.whiteLabeling,
          createLogoComponentFn: () => {
            return (
              <div className="flex items-center gap-2">
                {custom.logoUrl && (
                  <img
                    src={custom.logoUrl}
                    alt="Logo"
                    className="max-h-[30px] w-auto object-contain"
                  />
                )}
                {custom.systemName && (
                  <span className="text-base font-semibold tracking-wide text-white">
                    {custom.systemName}
                  </span>
                )}
              </div>
            );
          },
        };
      } else {
        if (enriched.whiteLabeling) {
          const newWhiteLabeling = { ...enriched.whiteLabeling };
          delete newWhiteLabeling.createLogoComponentFn;
          enriched.whiteLabeling = newWhiteLabeling;
        }
      }
    }

    const aiPanelStored = localStorage.getItem('ohif_enable_ai_panel');
    enriched.enableAiPanel = aiPanelStored === 'true';

    const rightPanelStored = localStorage.getItem('ohif_enable_right_panel');
    enriched.enableRightPanel = rightPanelStored === 'true';

    return enriched;
  } catch (e) {
    console.error('Error parsing custom branding from localStorage', e);
  }
  return config;
};

export function AppConfigProvider({ children, value: initAppConfig }) {
  const [appConfig, setAppConfig] = useState(() => enrichAppConfigWithBranding(initAppConfig));

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        let response;
        try {
          response = await fetch('/api/config/branding');
        } catch (_) {
          const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
          response = await fetch(`http://${host}:3000/api/config/branding`);
        }
        if (response.ok) {
          const custom = await response.json();
          try {
            localStorage.setItem('ohif_custom_branding', JSON.stringify(custom));
            if (custom.enableAiPanel !== undefined) {
              localStorage.setItem('ohif_enable_ai_panel', String(custom.enableAiPanel));
            }
            if (custom.enableRightPanel !== undefined) {
              localStorage.setItem('ohif_enable_right_panel', String(custom.enableRightPanel));
            }
          } catch (e) {
            console.error('Failed to cache branding in localStorage', e);
          }

          setAppConfig((prev: any) => {
            const enriched = { ...prev };
            if (custom.websiteTitle !== undefined) {
              enriched.websiteTitle = custom.websiteTitle;
            }
            if (custom.systemName !== undefined) {
              enriched.systemName = custom.systemName;
            }
            if (custom.logoUrl !== undefined) {
              enriched.logoUrl = custom.logoUrl;
            }
            if (custom.faviconUrl !== undefined) {
              enriched.faviconUrl = custom.faviconUrl;
            }
            if (custom.enableAiPanel !== undefined) {
              enriched.enableAiPanel = custom.enableAiPanel;
            }
            if (custom.enableRightPanel !== undefined) {
              enriched.enableRightPanel = custom.enableRightPanel;
            }

            if (custom.systemName || custom.logoUrl) {
              enriched.whiteLabeling = {
                ...enriched.whiteLabeling,
                createLogoComponentFn: () => {
                  return (
                    <div className="flex items-center gap-2">
                      {custom.logoUrl && (
                        <img
                          src={custom.logoUrl}
                          alt="Logo"
                          className="max-h-[30px] w-auto object-contain"
                        />
                      )}
                      {custom.systemName && (
                        <span className="text-base font-semibold tracking-wide text-white">
                          {custom.systemName}
                        </span>
                      )}
                    </div>
                  );
                },
              };
            } else {
              if (custom.systemName === "" && custom.logoUrl === "") {
                if (enriched.whiteLabeling) {
                  delete enriched.whiteLabeling.createLogoComponentFn;
                }
              }
            }
            return enriched;
          });
        }
      } catch (e) {
        console.error('Failed to fetch remote branding configuration from backend:', e);
      }
    };
    fetchBranding();
  }, []);

  useEffect(() => {
    const title = appConfig?.websiteTitle || 'OHIF Viewer';
    document.title = title;
  }, [appConfig?.websiteTitle]);

  useEffect(() => {
    const favicon = appConfig?.faviconUrl;
    const links = document.querySelectorAll("link[rel*='icon']");
    if (favicon) {
      links.forEach((link: any) => {
        link.href = favicon;
      });
    } else {
      // Revert to defaults
      links.forEach((link: any) => {
        if (link.rel.includes('shortcut')) {
          link.href = 'assets/favicon.ico';
        } else if (link.href.includes('16x16')) {
          link.href = 'assets/favicon-16x16.png';
        } else if (link.href.includes('32x32')) {
          link.href = 'assets/favicon-32x32.png';
        } else {
          link.href = 'assets/favicon.ico';
        }
      });
    }
  }, [appConfig?.faviconUrl]);

  return <Provider value={[appConfig, setAppConfig]}>{children}</Provider>;
}

AppConfigProvider.propTypes = {
  children: PropTypes.any,
  value: PropTypes.any,
};

export default AppConfigProvider;

